// Binary WebSocket protocol for JanneCraft multiplayer
// Shared between server and client

export interface PlayerAppearance {
  hairColor: number;
  skinColor: number;
  shirtColor: number;
  pantsColor: number;
  shoeColor: number;
  modelType: number;
}

export const MODEL_TYPES = [
  { id: 0, key: 'boy', label: 'Jongen' },
  { id: 1, key: 'girl', label: 'Meisje' },
  { id: 2, key: 'robot', label: 'Robot' },
  { id: 3, key: 'skeleton', label: 'Skelet' },
  { id: 4, key: 'alien', label: 'Alien' },
  { id: 5, key: 'pumpkin', label: 'Pompoen' },
  { id: 6, key: 'ninja', label: 'Ninja' },
] as const;

export const HAIR_PRESETS = [
  0x1a1a2e, 0x333333, 0x8b4513, 0xc0392b, 0xe6b800,
  0xd4a017, 0xb07030, 0x2c2c2c, 0xf5f5dc, 0x5c3317,
];

export const SKIN_PRESETS = [
  0xf8d5b8, 0xf5d0b0, 0xf0c8a0, 0xe8b88a,
  0xf2c8a8, 0xeebb99, 0xd4a373, 0xc68642,
  0x8d5524, 0x6b3a2a,
];

export const SHIRT_PRESETS = [
  0xe74c3c, 0x3498db, 0x2ecc71, 0xf39c12,
  0x9b59b6, 0x1abc9c, 0xe67e22, 0xecf0f1,
  0x2c3e50, 0xe84393,
];

export const PANTS_PRESETS = [
  0x2c3e50, 0x1a5276, 0x555555, 0x154360,
  0x6c3483, 0x7f8c8d, 0x34495e, 0x1e272e,
];

export const SHOE_PRESETS = [
  0x333333, 0x1a1a1a, 0x5d4037, 0x8b4513,
  0x2c2c2c, 0xecf0f1, 0xc0392b, 0x2980b9,
];

export function defaultAppearance(id: number): PlayerAppearance {
  return {
    hairColor: HAIR_PRESETS[id % HAIR_PRESETS.length],
    skinColor: SKIN_PRESETS[id % SKIN_PRESETS.length],
    shirtColor: SHIRT_PRESETS[id % SHIRT_PRESETS.length],
    pantsColor: PANTS_PRESETS[id % PANTS_PRESETS.length],
    shoeColor: SHOE_PRESETS[id % SHOE_PRESETS.length],
    modelType: 0,
  };
}

export const MessageType = {
  ChunkRequest:    0x01,
  ChunkData:       0x02,
  PlayerPosition:  0x03,
  PlayerPositions: 0x04,
  BlockModify:     0x05,
  BlockUpdate:     0x06,
  Welcome:         0x07,
  PlayerJoin:      0x08,
  PlayerLeave:     0x09,
  ChatMessage:     0x0A,
  TimeSync:        0x0B,
  NPCSpawn:        0x0C,
  NPCPositions:    0x0D,
  InventorySync:   0x0E,
  WorldList:       0x10,
  WorldJoin:       0x11,
  WorldJoinResult: 0x12,
  WorldLeave:      0x13,
  WorldCreate:     0x14,
  WorldDelete:     0x15,
  PortalActivate:  0x16,
  PortalTeleport:  0x17,
  PortalSync:      0x18,
  BedInteract:     0x19,
  SleepSync:       0x1A,
  TeleportBlockActivate: 0x1B,
  TeleportBlockResult:   0x1C,
  TNTActivate:     0x1D,
  TNTExplode:      0x1E,
  TNTFuse:         0x1F,
} as const;

// --- RLE Compression for chunk block data ---
// Format: [blockId, count(u16)] pairs
// Very effective for voxel data (lots of air runs)

export function rleEncode(data: Uint8Array): Uint8Array {
  const out: number[] = [];
  let i = 0;
  while (i < data.length) {
    const val = data[i];
    let count = 1;
    while (i + count < data.length && data[i + count] === val && count < 65535) {
      count++;
    }
    out.push(val);
    out.push(count >> 8, count & 0xff); // u16 big-endian
    i += count;
  }
  return new Uint8Array(out);
}

export function rleDecode(encoded: Uint8Array, outLength: number): Uint8Array {
  const out = new Uint8Array(outLength);
  let readPos = 0;
  let writePos = 0;
  while (readPos < encoded.length && writePos < outLength) {
    const val = encoded[readPos++];
    const count = (encoded[readPos++] << 8) | encoded[readPos++];
    for (let j = 0; j < count && writePos < outLength; j++) {
      out[writePos++] = val;
    }
  }
  return out;
}

// --- Message Encoders ---

export function encodeChunkRequest(cx: number, cz: number): ArrayBuffer {
  const buf = new ArrayBuffer(9);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.ChunkRequest);
  view.setInt32(1, cx);
  view.setInt32(5, cz);
  return buf;
}

export function encodeChunkData(cx: number, cz: number, compressedBlocks: Uint8Array, maxY: number): ArrayBuffer {
  const buf = new ArrayBuffer(13 + compressedBlocks.length);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.ChunkData);
  view.setInt32(1, cx);
  view.setInt32(5, cz);
  view.setInt32(9, maxY);
  new Uint8Array(buf, 13).set(compressedBlocks);
  return buf;
}

export function encodePlayerPosition(x: number, y: number, z: number, yaw: number, pitch: number): ArrayBuffer {
  const buf = new ArrayBuffer(21);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PlayerPosition);
  view.setFloat32(1, x);
  view.setFloat32(5, y);
  view.setFloat32(9, z);
  view.setFloat32(13, yaw);
  view.setFloat32(17, pitch);
  return buf;
}

export interface PlayerPosData {
  id: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export function encodePlayerPositions(players: PlayerPosData[]): ArrayBuffer {
  const buf = new ArrayBuffer(3 + players.length * 22);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PlayerPositions);
  view.setUint16(1, players.length);
  let offset = 3;
  for (const p of players) {
    view.setUint16(offset, p.id); offset += 2;
    view.setFloat32(offset, p.x); offset += 4;
    view.setFloat32(offset, p.y); offset += 4;
    view.setFloat32(offset, p.z); offset += 4;
    view.setFloat32(offset, p.yaw); offset += 4;
    view.setFloat32(offset, p.pitch); offset += 4;
  }
  return buf;
}

export function encodeBlockModify(wx: number, wy: number, wz: number, blockId: number): ArrayBuffer {
  const buf = new ArrayBuffer(14);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.BlockModify);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  view.setUint8(13, blockId);
  return buf;
}

export function encodeBlockUpdate(wx: number, wy: number, wz: number, blockId: number): ArrayBuffer {
  const buf = new ArrayBuffer(14);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.BlockUpdate);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  view.setUint8(13, blockId);
  return buf;
}

export function encodeWelcome(playerId: number, seed: number, spawnX: number, spawnY: number, spawnZ: number): ArrayBuffer {
  const buf = new ArrayBuffer(19);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.Welcome);
  view.setUint16(1, playerId);
  view.setUint32(3, seed);
  view.setFloat32(7, spawnX);
  view.setFloat32(11, spawnY);
  view.setFloat32(15, spawnZ);
  return buf;
}

export function encodePlayerJoin(id: number, name: string, appearance?: PlayerAppearance): ArrayBuffer {
  const nameBytes = new TextEncoder().encode(name);
  const hasAppearance = appearance ? 1 : 0;
  const extraBytes = hasAppearance ? 1 + 1 + 15 : 1; // flag + modelType(u8) + 5 x 3-byte colors
  const buf = new ArrayBuffer(4 + nameBytes.length + extraBytes);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PlayerJoin);
  view.setUint16(1, id);
  view.setUint8(3, nameBytes.length);
  new Uint8Array(buf, 4).set(nameBytes);
  let offset = 4 + nameBytes.length;
  view.setUint8(offset, hasAppearance); offset += 1;
  if (appearance) {
    view.setUint8(offset, appearance.modelType ?? 0); offset += 1;
    writeColor24(view, offset, appearance.hairColor); offset += 3;
    writeColor24(view, offset, appearance.skinColor); offset += 3;
    writeColor24(view, offset, appearance.shirtColor); offset += 3;
    writeColor24(view, offset, appearance.pantsColor); offset += 3;
    writeColor24(view, offset, appearance.shoeColor); offset += 3;
  }
  return buf;
}

function writeColor24(view: DataView, offset: number, color: number) {
  view.setUint8(offset, (color >> 16) & 0xff);
  view.setUint8(offset + 1, (color >> 8) & 0xff);
  view.setUint8(offset + 2, color & 0xff);
}

function readColor24(view: DataView, offset: number): number {
  return (view.getUint8(offset) << 16) | (view.getUint8(offset + 1) << 8) | view.getUint8(offset + 2);
}

export function encodePlayerLeave(id: number): ArrayBuffer {
  const buf = new ArrayBuffer(3);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PlayerLeave);
  view.setUint16(1, id);
  return buf;
}

// --- Message Decoders ---

export function decodeMessage(data: ArrayBuffer): { type: number; view: DataView; bytes: Uint8Array } {
  const view = new DataView(data);
  const bytes = new Uint8Array(data);
  return { type: view.getUint8(0), view, bytes };
}

export function decodeChunkRequest(view: DataView): { cx: number; cz: number } {
  return { cx: view.getInt32(1), cz: view.getInt32(5) };
}

export function decodeChunkData(view: DataView, bytes: Uint8Array): { cx: number; cz: number; maxY: number; compressedBlocks: Uint8Array } {
  return {
    cx: view.getInt32(1),
    cz: view.getInt32(5),
    maxY: view.getInt32(9),
    compressedBlocks: bytes.slice(13),
  };
}

export function decodePlayerPosition(view: DataView): { x: number; y: number; z: number; yaw: number; pitch: number } {
  return {
    x: view.getFloat32(1),
    y: view.getFloat32(5),
    z: view.getFloat32(9),
    yaw: view.getFloat32(13),
    pitch: view.getFloat32(17),
  };
}

export function decodePlayerPositions(view: DataView): PlayerPosData[] {
  const count = view.getUint16(1);
  const players: PlayerPosData[] = [];
  let offset = 3;
  for (let i = 0; i < count; i++) {
    players.push({
      id: view.getUint16(offset),
      x: view.getFloat32(offset + 2),
      y: view.getFloat32(offset + 6),
      z: view.getFloat32(offset + 10),
      yaw: view.getFloat32(offset + 14),
      pitch: view.getFloat32(offset + 18),
    });
    offset += 22;
  }
  return players;
}

export function decodeBlockModify(view: DataView): { wx: number; wy: number; wz: number; blockId: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
    blockId: view.getUint8(13),
  };
}

export function decodeBlockUpdate(view: DataView): { wx: number; wy: number; wz: number; blockId: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
    blockId: view.getUint8(13),
  };
}

export function decodeWelcome(view: DataView): { playerId: number; seed: number; spawnX: number; spawnY: number; spawnZ: number } {
  return {
    playerId: view.getUint16(1),
    seed: view.getUint32(3),
    spawnX: view.getFloat32(7),
    spawnY: view.getFloat32(11),
    spawnZ: view.getFloat32(15),
  };
}

export function decodePlayerJoin(view: DataView, bytes: Uint8Array): { id: number; name: string; appearance?: PlayerAppearance } {
  const nameLen = view.getUint8(3);
  const name = new TextDecoder().decode(bytes.slice(4, 4 + nameLen));
  let appearance: PlayerAppearance | undefined;
  const flagOffset = 4 + nameLen;
  if (flagOffset < bytes.length && view.getUint8(flagOffset) === 1) {
    const off = flagOffset + 1;
    // Check if we have the new format (modelType byte + 15 color bytes = 16) or old (15 color bytes)
    const remaining = bytes.length - off;
    if (remaining >= 16) {
      // New format: modelType(u8) + 5 colors
      appearance = {
        modelType: view.getUint8(off),
        hairColor: readColor24(view, off + 1),
        skinColor: readColor24(view, off + 4),
        shirtColor: readColor24(view, off + 7),
        pantsColor: readColor24(view, off + 10),
        shoeColor: readColor24(view, off + 13),
      };
    } else {
      // Old format: just 5 colors (backward compat)
      appearance = {
        modelType: 0,
        hairColor: readColor24(view, off),
        skinColor: readColor24(view, off + 3),
        shirtColor: readColor24(view, off + 6),
        pantsColor: readColor24(view, off + 9),
        shoeColor: readColor24(view, off + 12),
      };
    }
  }
  return { id: view.getUint16(1), name, appearance };
}

export function decodePlayerLeave(view: DataView): { id: number } {
  return { id: view.getUint16(1) };
}

// --- Chat message (0x0A) ---

// Client → Server: [0x0A][msgLen(u8)][message(utf8)]
export function encodeChatSend(message: string): ArrayBuffer {
  const msgBytes = new TextEncoder().encode(message);
  const buf = new ArrayBuffer(2 + msgBytes.length);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.ChatMessage);
  view.setUint8(1, msgBytes.length);
  new Uint8Array(buf, 2).set(msgBytes);
  return buf;
}

// Server → Client: [0x0A][nameLen(u8)][name(utf8)][msgLen(u8)][message(utf8)]
export function encodeChatBroadcast(name: string, message: string): ArrayBuffer {
  const nameBytes = new TextEncoder().encode(name);
  const msgBytes = new TextEncoder().encode(message);
  const buf = new ArrayBuffer(3 + nameBytes.length + msgBytes.length);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.ChatMessage);
  view.setUint8(1, nameBytes.length);
  new Uint8Array(buf, 2).set(nameBytes);
  view.setUint8(2 + nameBytes.length, msgBytes.length);
  new Uint8Array(buf, 3 + nameBytes.length).set(msgBytes);
  return buf;
}

export function decodeChatSend(bytes: Uint8Array): { message: string } {
  const msgLen = bytes[1];
  const message = new TextDecoder().decode(bytes.slice(2, 2 + msgLen));
  return { message };
}

export function decodeChatBroadcast(bytes: Uint8Array): { name: string; message: string } {
  const nameLen = bytes[1];
  const name = new TextDecoder().decode(bytes.slice(2, 2 + nameLen));
  const msgLen = bytes[2 + nameLen];
  const message = new TextDecoder().decode(bytes.slice(3 + nameLen, 3 + nameLen + msgLen));
  return { name, message };
}

// --- Time sync (0x0B) ---
// Server → Client: [0x0B][time(f32)]  (time is 0.0-1.0 day cycle fraction)

export function encodeTimeSync(time: number): ArrayBuffer {
  const buf = new ArrayBuffer(5);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.TimeSync);
  view.setFloat32(1, time);
  return buf;
}

export function decodeTimeSync(view: DataView): { time: number } {
  return { time: view.getFloat32(1) };
}

// --- NPC sync messages ---

// NPC entity types encoded as u8
export const NPCEntityType = {
  // Characters (0-8 = index into CHARACTERS array)
  Rosie: 0, Marcelle: 1, Onno: 2, Jules: 3, Mathilda: 4,
  GroteFloor: 5, Floor: 6, Minne: 7, Dores: 8,
  // Animals (100+)
  Pig: 100, Cow: 101, Bunny: 102, Lamb: 103, RedPanda: 104, Parrot: 105,
} as const;

export interface NPCSpawnData {
  id: number;       // u16
  entityType: number; // u8
  x: number;
  y: number;
  z: number;
  facing: number;
}

export interface NPCPosData {
  id: number;       // u16
  x: number;
  y: number;
  z: number;
  facing: number;
  state: number;    // 0=idle, 1=walking
}

// Server → Client: full spawn list (sent on connect)
// [0x0C][count(u16)][id(u16),entityType(u8),x(f32),y(f32),z(f32),facing(f32)]...
export function encodeNPCSpawn(npcs: NPCSpawnData[]): ArrayBuffer {
  const perNPC = 2 + 1 + 4 + 4 + 4 + 4; // 19 bytes each
  const buf = new ArrayBuffer(3 + npcs.length * perNPC);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.NPCSpawn);
  view.setUint16(1, npcs.length);
  let offset = 3;
  for (const n of npcs) {
    view.setUint16(offset, n.id); offset += 2;
    view.setUint8(offset, n.entityType); offset += 1;
    view.setFloat32(offset, n.x); offset += 4;
    view.setFloat32(offset, n.y); offset += 4;
    view.setFloat32(offset, n.z); offset += 4;
    view.setFloat32(offset, n.facing); offset += 4;
  }
  return buf;
}

export function decodeNPCSpawn(view: DataView): NPCSpawnData[] {
  const count = view.getUint16(1);
  const npcs: NPCSpawnData[] = [];
  let offset = 3;
  for (let i = 0; i < count; i++) {
    npcs.push({
      id: view.getUint16(offset),
      entityType: view.getUint8(offset + 2),
      x: view.getFloat32(offset + 3),
      y: view.getFloat32(offset + 7),
      z: view.getFloat32(offset + 11),
      facing: view.getFloat32(offset + 15),
    });
    offset += 19;
  }
  return npcs;
}

// Server → Client: position updates (sent at tick rate)
// [0x0D][count(u16)][id(u16),x(f32),y(f32),z(f32),facing(f32),state(u8)]...
export function encodeNPCPositions(npcs: NPCPosData[]): ArrayBuffer {
  const perNPC = 2 + 4 + 4 + 4 + 4 + 1; // 19 bytes each
  const buf = new ArrayBuffer(3 + npcs.length * perNPC);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.NPCPositions);
  view.setUint16(1, npcs.length);
  let offset = 3;
  for (const n of npcs) {
    view.setUint16(offset, n.id); offset += 2;
    view.setFloat32(offset, n.x); offset += 4;
    view.setFloat32(offset, n.y); offset += 4;
    view.setFloat32(offset, n.z); offset += 4;
    view.setFloat32(offset, n.facing); offset += 4;
    view.setUint8(offset, n.state); offset += 1;
  }
  return buf;
}

export function decodeNPCPositions(view: DataView): NPCPosData[] {
  const count = view.getUint16(1);
  const npcs: NPCPosData[] = [];
  let offset = 3;
  for (let i = 0; i < count; i++) {
    npcs.push({
      id: view.getUint16(offset),
      x: view.getFloat32(offset + 2),
      y: view.getFloat32(offset + 6),
      z: view.getFloat32(offset + 10),
      facing: view.getFloat32(offset + 14),
      state: view.getUint8(offset + 18),
    });
    offset += 19;
  }
  return npcs;
}

// --- Inventory sync (0x0E) ---
// Bidirectional: server sends on connect, client sends on changes
// Format: [0x0E][count(u16)][itemId(u8), itemCount(u16)]...

export interface InventoryItem {
  itemId: number;
  count: number;
}

export function encodeInventorySync(items: InventoryItem[]): ArrayBuffer {
  const buf = new ArrayBuffer(3 + items.length * 3);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.InventorySync);
  view.setUint16(1, items.length);
  let offset = 3;
  for (const item of items) {
    view.setUint8(offset, item.itemId); offset += 1;
    view.setUint16(offset, item.count); offset += 2;
  }
  return buf;
}

export function decodeInventorySync(view: DataView): InventoryItem[] {
  const count = view.getUint16(1);
  const items: InventoryItem[] = [];
  let offset = 3;
  for (let i = 0; i < count; i++) {
    items.push({
      itemId: view.getUint8(offset),
      count: view.getUint16(offset + 1),
    });
    offset += 3;
  }
  return items;
}

// --- World messages (0x10-0x15) ---

export interface WorldListEntry {
  id: string;
  name: string;
  playerCount: number;
  maxPlayers: number;
  hasPassword: boolean;
  isDefault: boolean;
}

// Server → Client: [0x10][count(u16)][id_len(u8),id(utf8),name_len(u8),name(utf8),players(u16),max(u16),has_pw(u8),is_default(u8)]...
export function encodeWorldList(worlds: WorldListEntry[]): ArrayBuffer {
  const encoder = new TextEncoder();
  const entries = worlds.map(w => ({
    idBytes: encoder.encode(w.id),
    nameBytes: encoder.encode(w.name),
    ...w,
  }));
  const totalSize = 3 + entries.reduce((sum, e) => sum + 1 + e.idBytes.length + 1 + e.nameBytes.length + 2 + 2 + 1 + 1, 0);
  const buf = new ArrayBuffer(totalSize);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  view.setUint8(0, MessageType.WorldList);
  view.setUint16(1, worlds.length);
  let offset = 3;
  for (const e of entries) {
    view.setUint8(offset, e.idBytes.length); offset += 1;
    bytes.set(e.idBytes, offset); offset += e.idBytes.length;
    view.setUint8(offset, e.nameBytes.length); offset += 1;
    bytes.set(e.nameBytes, offset); offset += e.nameBytes.length;
    view.setUint16(offset, e.playerCount); offset += 2;
    view.setUint16(offset, e.maxPlayers); offset += 2;
    view.setUint8(offset, e.hasPassword ? 1 : 0); offset += 1;
    view.setUint8(offset, e.isDefault ? 1 : 0); offset += 1;
  }
  return buf;
}

export function decodeWorldList(bytes: Uint8Array): WorldListEntry[] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const count = view.getUint16(1);
  const decoder = new TextDecoder();
  const worlds: WorldListEntry[] = [];
  let offset = 3;
  for (let i = 0; i < count; i++) {
    const idLen = view.getUint8(offset); offset += 1;
    const id = decoder.decode(bytes.slice(offset, offset + idLen)); offset += idLen;
    const nameLen = view.getUint8(offset); offset += 1;
    const name = decoder.decode(bytes.slice(offset, offset + nameLen)); offset += nameLen;
    const playerCount = view.getUint16(offset); offset += 2;
    const maxPlayers = view.getUint16(offset); offset += 2;
    const hasPassword = view.getUint8(offset) === 1; offset += 1;
    const isDefault = view.getUint8(offset) === 1; offset += 1;
    worlds.push({ id, name, playerCount, maxPlayers, hasPassword, isDefault });
  }
  return worlds;
}

// Client → Server: [0x11][id_len(u8),world_id(utf8),pw_len(u8),password(utf8)]
export function encodeWorldJoin(worldId: string, password?: string): ArrayBuffer {
  const encoder = new TextEncoder();
  const idBytes = encoder.encode(worldId);
  const pwBytes = encoder.encode(password ?? '');
  const buf = new ArrayBuffer(3 + idBytes.length + pwBytes.length);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  view.setUint8(0, MessageType.WorldJoin);
  view.setUint8(1, idBytes.length);
  bytes.set(idBytes, 2);
  view.setUint8(2 + idBytes.length, pwBytes.length);
  bytes.set(pwBytes, 3 + idBytes.length);
  return buf;
}

export function decodeWorldJoin(bytes: Uint8Array): { worldId: string; password: string } {
  const decoder = new TextDecoder();
  const idLen = bytes[1];
  const worldId = decoder.decode(bytes.slice(2, 2 + idLen));
  const pwLen = bytes[2 + idLen];
  const password = decoder.decode(bytes.slice(3 + idLen, 3 + idLen + pwLen));
  return { worldId, password };
}

// Server → Client: [0x12][success(u8)][reason_len(u8)][reason(utf8)]
export function encodeWorldJoinResult(success: boolean, reason?: string): ArrayBuffer {
  const encoder = new TextEncoder();
  const reasonBytes = encoder.encode(reason ?? '');
  const buf = new ArrayBuffer(3 + reasonBytes.length);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  view.setUint8(0, MessageType.WorldJoinResult);
  view.setUint8(1, success ? 1 : 0);
  view.setUint8(2, reasonBytes.length);
  bytes.set(reasonBytes, 3);
  return buf;
}

export function decodeWorldJoinResult(bytes: Uint8Array): { success: boolean; reason: string } {
  const decoder = new TextDecoder();
  const success = bytes[1] === 1;
  const reasonLen = bytes[2];
  const reason = decoder.decode(bytes.slice(3, 3 + reasonLen));
  return { success, reason };
}

// Client → Server: [0x13]
export function encodeWorldLeave(): ArrayBuffer {
  const buf = new ArrayBuffer(1);
  new DataView(buf).setUint8(0, MessageType.WorldLeave);
  return buf;
}

// Client → Server: [0x14][name_len(u8),name(utf8),seed_len(u8),seed(utf8),pw_len(u8),password(utf8)]
export function encodeWorldCreate(name: string, seed?: string, password?: string): ArrayBuffer {
  const encoder = new TextEncoder();
  const nameBytes = encoder.encode(name);
  const seedBytes = encoder.encode(seed ?? '');
  const pwBytes = encoder.encode(password ?? '');
  const buf = new ArrayBuffer(4 + nameBytes.length + seedBytes.length + pwBytes.length);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  view.setUint8(0, MessageType.WorldCreate);
  let offset = 1;
  view.setUint8(offset, nameBytes.length); offset += 1;
  bytes.set(nameBytes, offset); offset += nameBytes.length;
  view.setUint8(offset, seedBytes.length); offset += 1;
  bytes.set(seedBytes, offset); offset += seedBytes.length;
  view.setUint8(offset, pwBytes.length); offset += 1;
  bytes.set(pwBytes, offset);
  return buf;
}

export function decodeWorldCreate(bytes: Uint8Array): { name: string; seed: string; password: string } {
  const decoder = new TextDecoder();
  let offset = 1;
  const nameLen = bytes[offset]; offset += 1;
  const name = decoder.decode(bytes.slice(offset, offset + nameLen)); offset += nameLen;
  const seedLen = bytes[offset]; offset += 1;
  const seed = decoder.decode(bytes.slice(offset, offset + seedLen)); offset += seedLen;
  const pwLen = bytes[offset]; offset += 1;
  const password = decoder.decode(bytes.slice(offset, offset + pwLen));
  return { name, seed, password };
}

// Client → Server: [0x15][id_len(u8),world_id(utf8)]
export function encodeWorldDelete(worldId: string): ArrayBuffer {
  const encoder = new TextEncoder();
  const idBytes = encoder.encode(worldId);
  const buf = new ArrayBuffer(2 + idBytes.length);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);
  view.setUint8(0, MessageType.WorldDelete);
  view.setUint8(1, idBytes.length);
  bytes.set(idBytes, 2);
  return buf;
}

export function decodeWorldDelete(bytes: Uint8Array): { worldId: string } {
  const decoder = new TextDecoder();
  const idLen = bytes[1];
  const worldId = decoder.decode(bytes.slice(2, 2 + idLen));
  return { worldId };
}

// --- Portal messages (0x16-0x18) ---

// Client → Server: [0x16][wx(i32)][wy(i32)][wz(i32)]
export function encodePortalActivate(wx: number, wy: number, wz: number): ArrayBuffer {
  const buf = new ArrayBuffer(13);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PortalActivate);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  return buf;
}

export function decodePortalActivate(view: DataView): { wx: number; wy: number; wz: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
  };
}

// Server → Client: [0x17][x(f32)][y(f32)][z(f32)][yaw(f32)]
export function encodePortalTeleport(x: number, y: number, z: number, yaw: number): ArrayBuffer {
  const buf = new ArrayBuffer(17);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PortalTeleport);
  view.setFloat32(1, x);
  view.setFloat32(5, y);
  view.setFloat32(9, z);
  view.setFloat32(13, yaw);
  return buf;
}

export function decodePortalTeleport(view: DataView): { x: number; y: number; z: number; yaw: number } {
  return {
    x: view.getFloat32(1),
    y: view.getFloat32(5),
    z: view.getFloat32(9),
    yaw: view.getFloat32(13),
  };
}

// Server → Client: [0x18][count(u8)][originX(i32),originY(i32),originZ(i32),orientation(u8),pairId(i8),colorIndex(i8)]...
export interface PortalSyncEntry {
  id: number;
  originX: number;
  originY: number;
  originZ: number;
  orientation: string; // 'x' or 'z'
  pairId: number;
  colorIndex: number;
}

export function encodePortalSync(portals: PortalSyncEntry[]): ArrayBuffer {
  const perPortal = 2 + 4 + 4 + 4 + 1 + 1 + 1; // id(u16) + 3xi32 + orient(u8) + pairId(i8) + colorIndex(i8) = 17
  const buf = new ArrayBuffer(2 + portals.length * perPortal);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.PortalSync);
  view.setUint8(1, portals.length);
  let offset = 2;
  for (const p of portals) {
    view.setUint16(offset, p.id); offset += 2;
    view.setInt32(offset, p.originX); offset += 4;
    view.setInt32(offset, p.originY); offset += 4;
    view.setInt32(offset, p.originZ); offset += 4;
    view.setUint8(offset, p.orientation === 'z' ? 1 : 0); offset += 1;
    view.setInt8(offset, p.pairId); offset += 1;
    view.setInt8(offset, p.colorIndex); offset += 1;
  }
  return buf;
}

export function decodePortalSync(view: DataView): PortalSyncEntry[] {
  const count = view.getUint8(1);
  const portals: PortalSyncEntry[] = [];
  let offset = 2;
  for (let i = 0; i < count; i++) {
    portals.push({
      id: view.getUint16(offset),
      originX: view.getInt32(offset + 2),
      originY: view.getInt32(offset + 6),
      originZ: view.getInt32(offset + 10),
      orientation: view.getUint8(offset + 14) === 1 ? 'z' : 'x',
      pairId: view.getInt8(offset + 15),
      colorIndex: view.getInt8(offset + 16),
    });
    offset += 17;
  }
  return portals;
}

// --- Bed / Sleep messages (0x19-0x1A) ---

// Client → Server: [0x19][wx(i32)][wy(i32)][wz(i32)]
export function encodeBedInteract(wx: number, wy: number, wz: number): ArrayBuffer {
  const buf = new ArrayBuffer(13);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.BedInteract);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  return buf;
}

export function decodeBedInteract(view: DataView): { wx: number; wy: number; wz: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
  };
}

// Server → Client: [0x1A][sleepingCount(u16)][totalCount(u16)][isSelfSleeping(u8)]
export function encodeSleepSync(sleepingCount: number, totalCount: number, isSelfSleeping: boolean): ArrayBuffer {
  const buf = new ArrayBuffer(6);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.SleepSync);
  view.setUint16(1, sleepingCount);
  view.setUint16(3, totalCount);
  view.setUint8(5, isSelfSleeping ? 1 : 0);
  return buf;
}

export function decodeSleepSync(view: DataView): { sleepingCount: number; totalCount: number; isSelfSleeping: boolean } {
  return {
    sleepingCount: view.getUint16(1),
    totalCount: view.getUint16(3),
    isSelfSleeping: view.getUint8(5) === 1,
  };
}

// --- Teleport Block messages (0x1B-0x1C) ---

// Client → Server: [0x1B][wx(i32)][wy(i32)][wz(i32)]
export function encodeTeleportBlockActivate(wx: number, wy: number, wz: number): ArrayBuffer {
  const buf = new ArrayBuffer(13);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.TeleportBlockActivate);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  return buf;
}

export function decodeTeleportBlockActivate(view: DataView): { wx: number; wy: number; wz: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
  };
}

// Server → Client: [0x1C][x(f32)][y(f32)][z(f32)][yaw(f32)]
export function encodeTeleportBlockResult(x: number, y: number, z: number, yaw: number): ArrayBuffer {
  const buf = new ArrayBuffer(17);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.TeleportBlockResult);
  view.setFloat32(1, x);
  view.setFloat32(5, y);
  view.setFloat32(9, z);
  view.setFloat32(13, yaw);
  return buf;
}

export function decodeTeleportBlockResult(view: DataView): { x: number; y: number; z: number; yaw: number } {
  return {
    x: view.getFloat32(1),
    y: view.getFloat32(5),
    z: view.getFloat32(9),
    yaw: view.getFloat32(13),
  };
}

// --- TNT messages (0x1D-0x1E) ---

// Client → Server: [0x1D][wx(i32)][wy(i32)][wz(i32)]
export function encodeTNTActivate(wx: number, wy: number, wz: number): ArrayBuffer {
  const buf = new ArrayBuffer(13);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.TNTActivate);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  return buf;
}

export function decodeTNTActivate(view: DataView): { wx: number; wy: number; wz: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
  };
}

// Server → Client: [0x1E][x(f32)][y(f32)][z(f32)]
export function encodeTNTExplode(x: number, y: number, z: number): ArrayBuffer {
  const buf = new ArrayBuffer(13);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.TNTExplode);
  view.setFloat32(1, x);
  view.setFloat32(5, y);
  view.setFloat32(9, z);
  return buf;
}

export function decodeTNTExplode(view: DataView): { x: number; y: number; z: number } {
  return {
    x: view.getFloat32(1),
    y: view.getFloat32(5),
    z: view.getFloat32(9),
  };
}

// --- TNT Fuse (0x1F) ---

// Server → Client: [0x1F][wx(i32)][wy(i32)][wz(i32)][duration(f32)]
export function encodeTNTFuse(wx: number, wy: number, wz: number, duration: number): ArrayBuffer {
  const buf = new ArrayBuffer(17);
  const view = new DataView(buf);
  view.setUint8(0, MessageType.TNTFuse);
  view.setInt32(1, wx);
  view.setInt32(5, wy);
  view.setInt32(9, wz);
  view.setFloat32(13, duration);
  return buf;
}

export function decodeTNTFuse(view: DataView): { wx: number; wy: number; wz: number; duration: number } {
  return {
    wx: view.getInt32(1),
    wy: view.getInt32(5),
    wz: view.getInt32(9),
    duration: view.getFloat32(13),
  };
}
